import ApplicationController from "./application_controller";

import TomSelect from 'tom-select'
import 'tom-select/dist/css/tom-select.css'
import '../stylesheets/tom-select.scss'

export default class extends ApplicationController {
    static targets = [ "input" ]

    connect() {
        this.initializeTomSelect();
    }

    disconnect() {
        if (this.tomselect) {
            this.tomselect.destroy();
        }
    }

    initializeTomSelect() {
        // POLYGON: bereits gespeicherte Attendees (value "Typ:ID" -> Attendee-ID) merken,
        // bevor Tom Select die Optionen uebernimmt. So bleibt die DB-ID auch nach
        // Entfernen/erneutem Hinzufuegen erhalten (Uniqueness, vgl. IVM 72cbdfa16) und
        // entfernte Teilnehmer koennen per _destroy geloescht werden.
        this.persistedAttendeeIds = {};
        Array.from(this.inputTarget.options).forEach((option) => {
            if (option.dataset && option.dataset.attendeeId) {
                this.persistedAttendeeIds[option.value] = option.dataset.attendeeId;
            }
        });

        const isRequired = this.inputTarget.hasAttribute('required');
        const searchUrl = this.element.dataset.searchUrl || '/de/backend/meetings/search_attendees';

        let placeholder = this.inputTarget.dataset.placeholder ? this.inputTarget.dataset.placeholder : "Teilnehmer suchen...";
        
        // Sammle bereits ausgewählte Optionen
        const selectedOptions = Array.from(this.inputTarget.selectedOptions).map((option) => {
            const id = option.value;
            const typeFromValue =
                id && String(id).includes(':') ? String(id).split(':')[0] : undefined;
            return {
                id,
                text: option.text,
                type: typeFromValue
            };
        });
        
        let config = {
            plugins: {
                remove_button: {
                    title: 'Entfernen'
                }
            },
            create: false,
            valueField: 'id',
            labelField: 'text',
            searchField: ['text', 'email', 'type'],
            closeAfterSelect: true,
            selectOnTab: true,
            placeholder: placeholder,
            required: isRequired,
            validateOnBlur: true,
            maxItems: null,
            options: selectedOptions, // Verwende bereits ausgewählte Optionen
            onInvalid: function() {
                this.wrapper.classList.add('ts-error');
            },
            onValid: function() {
                this.wrapper.classList.remove('ts-error');
            },
            load: (query, callback) => {
                if (!query.length) return callback();
                
                let url = `${searchUrl}?q=${encodeURIComponent(query)}`;
                
                fetch(url)
                    .then(response => response.json())
                    .then(json => {
                        // Transformiere die Antwort für TomSelect
                        const transformedJson = json.map(item => ({
                            id: `${item.type}:${item.id}`,
                            text: item.name,
                            subtext: item.subtitle,
                            email: item.email,
                            type: item.type,
                            icon: item.icon
                        }));
                        callback(transformedJson);
                    }).catch(() => {
                        callback();
                    });
            },
            render: {
                option: function(data, escape) {
                    return `<div class="py-2 px-3 hover:bg-gray-50 flex items-center">
                        <span class="text-lg mr-3">${data.icon || '👤'}</span>
                        <div class="flex-1">
                            <div class="font-medium text-gray-900">${escape(data.text)}</div>
                            <div class="text-sm text-gray-600">
                                ${escape(data.email || '')} 
                                <span class="text-xs text-gray-500">• ${escape(data.subtext || '')}</span>
                            </div>
                        </div>
                    </div>`;
                },
                item: function(data, escape) {
                    const type =
                        data.type ||
                        (data.id && String(data.id).includes(':')
                            ? String(data.id).split(':')[0]
                            : '');
                    const typeSuffix = type ? ` <span class="text-xs text-gray-500">(${escape(type)})</span>` : '';
                    return `<div class="px-2 py-1 flex items-center">
                        <span class="text-sm mr-2">${data.icon || '👤'}</span>
                        <span>${escape(data.text)}</span>${typeSuffix}
                    </div>`;
                },
                no_results: function(data, escape) {
                    return '<div class="no-results p-2 text-sm text-gray-600">Keine Teilnehmer für "' + escape(data.input) + '" gefunden</div>';
                }
            }
        };

        this.tomselect = new TomSelect(this.inputTarget, config);
        
        // Konvertiere die Auswahl in das richtige Format für Rails
        this.inputTarget.addEventListener('change', (e) => {
            this.convertToRailsFormat();
        });
        
        // Konvertiere auch beim Laden der Seite
        this.convertToRailsFormat();
    }

    convertToRailsFormat() {
        const selectedValues = Array.from(this.inputTarget.selectedOptions).map(option => option.value);
        // Stimulus-Root (.form-group), nicht nur das Tom-Select-Wrapper-Element — sonst bleiben alte Hidden Fields stehen und es fehlt ggf. meeting[attendees_attributes][n][id] für Updates.
        const container = this.element;

        container.querySelectorAll('input[name*="attendees_attributes"]').forEach((input) => input.remove());

        selectedValues.forEach((value, index) => {
            const [type, id] = value.split(':');
            const option = Array.from(this.inputTarget.options).find((o) => o.value === value);
            const attendeeId = option?.dataset?.attendeeId || (this.persistedAttendeeIds || {})[value];

            if (attendeeId) {
                const idField = document.createElement('input');
                idField.type = 'hidden';
                idField.name = `meeting[attendees_attributes][${index}][id]`;
                idField.value = attendeeId;
                container.appendChild(idField);
            }

            const attendableTypeInput = document.createElement('input');
            attendableTypeInput.type = 'hidden';
            attendableTypeInput.name = `meeting[attendees_attributes][${index}][attendable_type]`;
            attendableTypeInput.value = type;
            container.appendChild(attendableTypeInput);

            const attendableIdInput = document.createElement('input');
            attendableIdInput.type = 'hidden';
            attendableIdInput.name = `meeting[attendees_attributes][${index}][attendable_id]`;
            attendableIdInput.value = id;
            container.appendChild(attendableIdInput);

            const organizerInput = document.createElement('input');
            organizerInput.type = 'hidden';
            organizerInput.name = `meeting[attendees_attributes][${index}][organizer]`;
            organizerInput.value =
                option?.dataset?.organizer !== undefined ? option.dataset.organizer : type === 'User' ? 'true' : 'false';
            container.appendChild(organizerInput);

            const responseStatusInput = document.createElement('input');
            responseStatusInput.type = 'hidden';
            responseStatusInput.name = `meeting[attendees_attributes][${index}][response_status]`;
            responseStatusInput.value =
                option?.dataset?.responseStatus !== undefined
                    ? option.dataset.responseStatus
                    : type === 'User'
                      ? 'accepted'
                      : 'pending';
            container.appendChild(responseStatusInput);

            const sendNotificationInput = document.createElement('input');
            sendNotificationInput.type = 'hidden';
            sendNotificationInput.name = `meeting[attendees_attributes][${index}][send_notification]`;
            sendNotificationInput.value =
                option?.dataset?.sendNotification !== undefined ? option.dataset.sendNotification : 'true';
            container.appendChild(sendNotificationInput);
        });

        // POLYGON: entfernte, bereits gespeicherte Teilnehmer loeschen (_destroy),
        // sonst bleiben sie beim Bearbeiten unsichtbar am Termin haengen.
        let destroyIndex = selectedValues.length;
        Object.keys(this.persistedAttendeeIds || {}).forEach((value) => {
            if (selectedValues.includes(value)) return;

            const idField = document.createElement('input');
            idField.type = 'hidden';
            idField.name = `meeting[attendees_attributes][${destroyIndex}][id]`;
            idField.value = this.persistedAttendeeIds[value];
            container.appendChild(idField);

            const destroyField = document.createElement('input');
            destroyField.type = 'hidden';
            destroyField.name = `meeting[attendees_attributes][${destroyIndex}][_destroy]`;
            destroyField.value = '1';
            container.appendChild(destroyField);

            destroyIndex += 1;
        });
    }

    destroyTomSelect() {
        if (this.tomselect) {
            this.tomselect.destroy();
        }
    }
}
